'use client';
import { Image, Skeleton } from '@bfe/moly';
import { invoke, runAsyncSafe } from '@web3/base-utils';
import clsx from 'clsx';
import {
  CSSProperties,
  ComponentProps,
  ReactNode,
  startTransition,
  useEffect,
  useRef,
  useState,
} from 'react';

export type ImageWithDefaultProps = Omit<
  ComponentProps<typeof Image>,
  'src'
> & {
  //
  fallback?: ReactNode | (() => ReactNode);
  fallbackByText?: string;
  fallbackImage?: string;
  fallbackImageClassName?: string;
  placeholderClassName?: string;
  placeholderStyle?: CSSProperties;
  skeleton?: boolean;
  size?: number;
  src?: string | { src: string } | { default: { src: string } };
};

export function ImageWithDefault({
  size,
  width = size,
  height = size,
  fallback,
  fallbackByText,
  fallbackImage,
  fallbackImageClassName,
  className,
  placeholderClassName,
  skeleton = true,
  placeholderStyle = {
    borderRadius: '50%',
    backgroundColor: ' var(--bds-gray-t4-dis)',
  },
  src,
  onError: _onError,
  onLoad,
  ...rest
}: ImageWithDefaultProps) {
  const isRounded = className?.includes('rounded-full');
  const [tryLoaded, setTryLoaded] = useState<unknown>();
  const [loading, setLoading] = useState<boolean>();
  const [renderImg, setRenderImg] = useState<string>();
  const isUnMounted = useRef(false);
  const widthNum = parsePixelNumber(width);
  const heightNum = parsePixelNumber(height);

  const sizeStyle = {
    width: widthNum ? `${widthNum}px` : width,
    height: heightNum ? `${heightNum}px` : height,
  };

  useEffect(() => {
    return () => {
      isUnMounted.current = true;
    };
  }, []);

  useEffect(() => {
    invoke(async () => {
      if (!renderImg) {
        setLoading(true);
      }
      const [err, img] = await runAsyncSafe(
        getCanRenderImgUrl(getSrcUrl(src), fallbackImage)
      );
      startTransition(() => {
        if (!isUnMounted.current) {
          setLoading(false);
        }
        setTryLoaded(true);
        if (img) {
          if (!isUnMounted.current) {
            setRenderImg(img);
          }
        } else {
          if (err) {
            _onError?.(err);
          }
        }
      });
    });
    // 这里就是为了在src 变化时有错误的情况下就清理错误，以达到重新加载的目的
  }, [src, fallbackImage]);

  if (!renderImg && tryLoaded) {
    if (fallback) {
      return typeof fallback === 'function' ? fallback() : fallback;
    }
    const fzSize = Math.min((widthNum || 24) / 2, heightNum ?? 24 / 2);

    return (
      <div
        className={clsx(
          'flex items-center justify-center',
          className,
          placeholderClassName
        )}
        style={{
          ...sizeStyle,
          ...placeholderStyle,
        }}
      >
        {fallbackByText ? (
          <span
            className="text-bds-gray-t1-title origin-center"
            style={{
              fontSize: `${fzSize}px`,
              transform: `scale(${Math.min(1, fzSize / 12)})`,
            }}
          >
            {fallbackByText[0]}
          </span>
        ) : (
          emptyImageIcon
        )}
      </div>
    );
  }

  if (!renderImg || (loading && skeleton)) {
    return isRounded ? (
      <Skeleton.Ball className={className} style={sizeStyle} />
    ) : (
      <Skeleton.Bar className={className} style={sizeStyle} />
    );
  }

  const handleImgUrl = (url?: string) => {
    if (!url) {
      return '';
    }
    try {
      // eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef
      const urlObj = new URL(url);
      return urlObj.href;
    } catch {
      return url;
    }
  };

  return (
    <Image
      alt=""
      loading="lazy"
      {...rest}
      className={clsx(
        'transition-all',
        className,
        fallbackImage === renderImg && fallbackImageClassName,
        loading ? 'opacity-0' : 'opacity-100'
      )}
      src={handleImgUrl(renderImg)}
      style={sizeStyle}
      onLoad={onLoad}
    />
  );
}

export function imageWithDefault(
  imgUrl: string | ReactNode | (() => ReactNode)
) {
  const addingProps =
    typeof imgUrl === 'string'
      ? { fallbackImage: imgUrl }
      : {
          fallback: imgUrl,
        };
  const c: React.FunctionComponent = (
    props: Omit<ImageWithDefaultProps, 'fallbackImage'>
  ) => {
    return <ImageWithDefault {...props} {...addingProps} />;
  };
  c.displayName = 'ImageWithDefault';
  return c;
}

let emptyImageIcon = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_11738_249531)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.5391 10.53C9.55098 10.53 10.3713 9.68385 10.3713 8.64002C10.3713 7.59619 9.55098 6.75 8.5391 6.75C7.52723 6.75 6.70694 7.59619 6.70694 8.64002C6.70694 9.68385 7.52723 10.53 8.5391 10.53ZM7.92159 11.825C8.13297 11.4795 8.47556 11.4795 8.68752 11.825L10.2835 14.4289L13.5835 9.10913C13.7973 8.7643 14.1423 8.76294 14.3572 9.10913L19.0205 16.6269C19.2336 16.971 19.1107 17.25 18.7432 17.25H5.25924C4.8923 17.25 4.76584 16.9703 4.9778 16.6255L7.92159 11.825Z"
        fill="#ADB1B8"
      />
    </g>
    <defs>
      <clipPath id="clip0_11738_249531">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
/** 设置默认的图片svg图标 */
export function setDefaultEmptyImageIcon(img: JSX.Element) {
  emptyImageIcon = img;
}

const preloadImg = async (url?: string) => {
  if (!url) {
    return;
  }
  return new Promise<void>((resolve, reject) => {
    // eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef
    const img = document.createElement('img');
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = url;
  });
};

const getCanRenderImgUrl = async (imgUrl?: string, fallbackImg?: string) => {
  const firstTry = imgUrl || fallbackImg;
  if (!firstTry) {
    return;
  }
  try {
    await preloadImg(firstTry);
    return firstTry;
  } catch (_err) {
    if (firstTry === fallbackImg || !fallbackImg) {
      return;
    }
    await preloadImg(fallbackImg);
    return fallbackImg;
  }
};

const getSrcUrl = (_s: any) => {
  if (!_s) {
    return '';
  }
  if (typeof _s === 'string') {
    return _s;
  }
  return _s?.src || _s?.default?.src;
};
const parsePixelNumber = (v?: number | string) => {
  const type = typeof v;
  if (type === 'undefined') {
    return NaN;
  }
  if (type === 'number') {
    return v as number;
  }
  return Number(String(v).replace('px', ''));
};
